import { useSetAtom } from 'jotai';
import { toaster } from '@optiaxiom/globals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  UpdateUserPreferencesRequest,
  userPreferencesService,
} from '@frontend/api/services/userPreferencesService';
import { FontSize, fontSizeAtom } from '@frontend/atoms/appearanceAtom';

const USER_PREFERENCES_QUERY_KEY = ['opal:user-preferences'];

export function useUserPreferences(userEmail: string | undefined) {
  const setFontSize = useSetAtom(fontSizeAtom);

  return useQuery({
    queryKey: [...USER_PREFERENCES_QUERY_KEY, userEmail],
    queryFn: async () => {
      const prefs = await userPreferencesService.getUserPreferences();
      setFontSize(prefs.font_size);
      return prefs;
    },
    enabled: !!userEmail,
  });
}

export function useUpdateUserPreferences(userEmail: string | undefined) {
  const queryClient = useQueryClient();
  const setFontSize = useSetAtom(fontSizeAtom);

  return useMutation({
    mutationFn: (prefs: UpdateUserPreferencesRequest) => {
      return userPreferencesService.updateUserPreferences(prefs);
    },
    onMutate: (prefs: UpdateUserPreferencesRequest) => {
      setFontSize(prefs.font_size);
    },
    onSuccess: () => {
      toaster.create('Preferences saved', { type: 'success' });
      queryClient.invalidateQueries({
        queryKey: [...USER_PREFERENCES_QUERY_KEY, userEmail],
      });
    },
  });
}

export function useSetFontSizeWithPersist(userEmail: string | undefined) {
  const { mutate } = useUpdateUserPreferences(userEmail);

  return (fontSize: FontSize) => {
    if (userEmail) {
      mutate({ font_size: fontSize });
    }
  };
}
